import React, { useRef, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import { cn } from "../../../utils/cn";

const VisualizerPanel = ({
  micState,
  isMuted,
  getAnalyserData,
  outputLevel,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  // Status message
  const getStatusText = () => {
    if (micState !== "connected") return "STANDBY";
    if (isMuted) return "MUTED";
    if (outputLevel > 15) return "SPEAKING";
    return "LISTENING";
  };

  const statusText = getStatusText();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Handle canvas resizing
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Render loop
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      const width = canvas.width;
      const height = canvas.height;

      // Clear with dark CRT background
      ctx.fillStyle = "#0d1117";
      ctx.fillRect(0, 0, width, height);

      // Draw faint terminal grid lines
      ctx.strokeStyle = "rgba(0, 255, 65, 0.03)";
      ctx.lineWidth = 1;
      const gridSize = 30;
      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      // Draw CRT scanlines
      ctx.strokeStyle = "rgba(0, 0, 0, 0.15)";
      ctx.lineWidth = 2;
      for (let y = 0; y < height; y += 4) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      const audioData = getAnalyserData();

      if (micState !== "connected" || !audioData) {
        // Draw flat standby lines
        ctx.strokeStyle = "rgba(0, 255, 65, 0.15)";
        ctx.shadowColor = "rgba(0, 255, 65, 0.4)";
        ctx.shadowBlur = 4;
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        ctx.moveTo(0, height / 2);
        ctx.lineTo(width, height / 2);
        ctx.stroke();

        ctx.shadowBlur = 0; // reset shadow
        return;
      }

      const { inputWaveform, outputFrequency } = audioData;

      // ==========================================
      // DRAW SPECTROGRAM BARS (Output Frequency)
      // ==========================================
      const barWidth = (width / outputFrequency.length) * 1.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < outputFrequency.length; i++) {
        barHeight = (outputFrequency[i] / 255) * (height * 0.7);

        // Gradient from cyan to primary green
        const grad = ctx.createLinearGradient(0, height, 0, height - barHeight);
        grad.addColorStop(0, "rgba(0, 255, 170, 0.05)");
        grad.addColorStop(0.5, "rgba(0, 255, 170, 0.2)");
        grad.addColorStop(1, "rgba(0, 255, 65, 0.35)");

        ctx.fillStyle = grad;
        
        // Draw bars from the bottom upwards
        ctx.fillRect(x, height - barHeight, barWidth - 1, barHeight);

        x += barWidth;
      }

      // ==========================================
      // DRAW OSCILLOSCOPE (Input Waveform)
      // ==========================================
      ctx.strokeStyle = isMuted ? "rgba(248, 81, 73, 0.7)" : "rgba(0, 255, 65, 0.85)";
      ctx.shadowColor = isMuted ? "rgba(248, 81, 73, 0.9)" : "rgba(0, 255, 65, 0.9)";
      ctx.shadowBlur = 6;
      ctx.lineWidth = 2.5;
      ctx.beginPath();

      const sliceWidth = width / inputWaveform.length;
      let ox = 0;

      for (let i = 0; i < inputWaveform.length; i++) {
        const v = inputWaveform[i] / 128.0; // normalised centered around 1.0
        const oy = (v * height) / 2;

        if (i === 0) {
          ctx.moveTo(ox, oy);
        } else {
          ctx.lineTo(ox, oy);
        }

        ox += sliceWidth;
      }

      ctx.lineTo(width, height / 2);
      ctx.stroke();
      ctx.shadowBlur = 0; // Reset shadow
    };

    draw();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [micState, getAnalyserData, isMuted, outputLevel]);

  // Status label styling
  const statusColors = {
    STANDBY: "text-muted-foreground bg-muted/10 border-border",
    MUTED: "text-destructive bg-destructive/10 border-destructive/30 shadow-[0_0_8px_rgba(248,81,73,0.3)]",
    LISTENING: "text-terminal-blue bg-terminal-blue/10 border-terminal-blue/30 shadow-[0_0_8px_rgba(74,158,255,0.3)]",
    SPEAKING: "text-primary bg-primary/10 border-primary/30 shadow-[0_0_12px_rgba(0,255,65,0.4)] animate-pulse",
  };

  return (
    <div
      ref={containerRef}
      className="bg-card border border-border rounded-lg p-6 flex flex-col h-full terminal-glow relative overflow-hidden"
    >
      {/* CRT Screen Header */}
      <div className="flex items-center justify-between border-b border-border pb-4 mb-4 z-10">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={18} className="text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-foreground font-fira">
            Visual Signal Matrix
          </h2>
        </div>

        {/* Dynamic Glowing Status Badge */}
        <div className={cn(
          "px-3 py-1 rounded border text-[10px] font-bold tracking-widest font-fira transition-all duration-300",
          statusColors[statusText]
        )}>
          {statusText}
        </div>
      </div>

      {/* Retro Canvas Visualizer */}
      <div className="flex-1 border border-border rounded bg-[#0d1117] overflow-hidden relative shadow-inner">
        <canvas ref={canvasRef} className="w-full h-full block" />
        
        {/* Subtle scanline overlay to enhance CRT feeling */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black/10 z-10" />
      </div>
    </div>
  );
};

export default VisualizerPanel;
